/**
 * Chat Input Component
 * Input field for typing and sending messages
 *
 * Accessibility:
 * - 44px minimum touch targets
 * - Keyboard shortcut documentation
 * - ARIA labels with state information
 * - Respects reduced motion preferences
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Send, Loader2 } from 'lucide-react';
import type { RateLimit } from '../types/chat.types';
import { cn } from '../../../../lib/utils';
import { useChatRateLimit } from '../hooks/useChatRateLimit';
import { RateLimitIndicator } from './RateLimitIndicator';
import { RateLimitProgressBar } from './RateLimitProgressBar';
import { BurstLimitIndicator } from './BurstLimitIndicator';
import { getIslamicGuidance } from '../utils/islamicGuidance';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';

interface ChatInputProps {
  onSend: (message: string) => Promise<void>;
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
  rateLimit?: RateLimit | null;
  rateLimitRetryAfter?: number | null;
  className?: string;
}

/**
 * Chat input field with send button
 * Auto-focuses when chat opens, handles Enter key
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  isStreaming = false,
  placeholder = 'Ask anything...',
  rateLimit = null,
  rateLimitRetryAfter = null,
  className
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { prefersReducedMotion } = useReducedMotion();

  // Use rate limit hook for enhanced state management
  const { rateLimitState, updateRateLimit, formatCountdown } = useChatRateLimit();

  // Update rate limit when prop changes
  useEffect(() => {
    updateRateLimit(rateLimit);
  }, [rateLimit, updateRateLimit]);

  // Auto-focus input when component mounts
  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  /**
   * Check if send button should be disabled due to rate limit
   */
  const isRateLimited = rateLimitState?.isRateLimited ?? false;
  const isDisabled = disabled || isSending || isStreaming || isRateLimited;

  // Get countdown display
  const countdownDisplay = rateLimitState?.secondsUntilReset
    ? formatCountdown(rateLimitState.secondsUntilReset)
    : rateLimitRetryAfter !== null
      ? formatCountdown(rateLimitRetryAfter)
      : null;

  // Get placeholder with Islamic guidance if rate limited
  const getPlaceholder = (): string => {
    if (isStreaming) {
      return 'Hajra is responding...';
    }
    if (isRateLimited && countdownDisplay) {
      return `Please wait ${countdownDisplay}...`;
    }
    if (rateLimitState && rateLimitState.shouldShowWarning) {
      return getIslamicGuidance('approaching_limit', rateLimitState.remaining);
    }
    return placeholder;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!message.trim() || isSending || isDisabled || isStreaming) return;

    const messageToSend = message.trim();
    setMessage('');

    try {
      setIsSending(true);
      await onSend(messageToSend);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessage(messageToSend); // Restore message on error
    } finally {
      setIsSending(false);
      // Re-focus input after sending
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  /**
   * Handle Enter key (send) or Shift+Enter (new line)
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  /**
   * Auto-resize textarea based on content
   */
  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>): void => {
    const target = e.currentTarget;
    const minHeight = 48;
    const maxHeight = 128;

    target.style.height = `${minHeight}px`;
    const newHeight = Math.min(Math.max(target.scrollHeight, minHeight), maxHeight);
    target.style.height = `${newHeight}px`;
  };

  // Generate accessible label for send button
  const getSendButtonLabel = (): string => {
    if (isSending) return 'Sending message...';
    if (isRateLimited && countdownDisplay) return `Rate limited. Wait ${countdownDisplay}`;
    if (isStreaming) return 'Waiting for response...';
    if (!message.trim()) return 'Type a message to send';
    return 'Send message (Enter)';
  };

  return (
    <div className="relative">
      {/* Rate Limit Progress Bar */}
      {rateLimitState && rateLimitState.shouldShowWarning && (
        <div className="px-4 pt-3 pb-2">
          <RateLimitProgressBar
            rateLimitState={rateLimitState}
            height="sm"
            showBurstOverlay={true}
          />
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={cn(
          'border-t border-gray-200/60',
          'bg-white/95 backdrop-blur-sm',
          'px-4 py-3',
          'rounded-bl-2xl max-md:rounded-none',
          // Safe area for devices with home indicator
          'max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]',
          className
        )}
        role="form"
        aria-label="Send a message"
      >
        {/* Rate Limit Indicators Row */}
        {rateLimitState && (
          <div
            className="flex items-center justify-between mb-2 gap-2"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center gap-2">
              <RateLimitIndicator
                rateLimitState={rateLimitState}
                size="sm"
                showIcon={true}
              />
              {rateLimitState.burstRemaining < rateLimitState.remaining && (
                <BurstLimitIndicator
                  rateLimitState={rateLimitState}
                  showTooltip={true}
                />
              )}
            </div>
            {isRateLimited && countdownDisplay && (
              <span className="text-xs text-red-600 dark:text-red-400 font-medium tabular-nums">
                Resets in {countdownDisplay}
              </span>
            )}
          </div>
        )}

        <div className="flex items-end gap-3">
          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={handleInput}
              disabled={isDisabled}
              placeholder={getPlaceholder()}
              rows={1}
              maxLength={5000}
              className={cn(
                'w-full min-h-[48px] h-12 max-h-32',
                'px-4 py-3',
                'text-base leading-normal', // 16px for readability
                'border border-gray-300/60 rounded-xl',
                'bg-gray-50/50 focus:bg-white',
                'resize-none overflow-y-auto',
                'transition-colors duration-150',
                // Focus states
                'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500',
                'hover:border-gray-400/60',
                // Disabled state
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100',
                // Mobile minimum size
                'max-md:min-h-[52px] max-md:h-13'
              )}
              aria-label="Message input"
              aria-describedby="input-help"
              aria-invalid={message.length >= 5000}
            />

            {/* Character count */}
            {message.length > 0 && (
              <span
                className={cn(
                  'absolute bottom-2.5 right-3',
                  'text-xs pointer-events-none tabular-nums',
                  message.length >= 4750 ? 'text-amber-600' : 'text-gray-400',
                  message.length >= 5000 && 'text-red-500'
                )}
                aria-hidden="true"
              >
                {message.length}/5000
              </span>
            )}
          </div>

          {/* Send Button */}
          <motion.button
            type="submit"
            disabled={!message.trim() || isDisabled}
            whileHover={prefersReducedMotion || !message.trim() || isDisabled ? {} : { scale: 1.02 }}
            whileTap={prefersReducedMotion || !message.trim() || isDisabled ? {} : { scale: 0.98 }}
            className={cn(
              // Size - meets 44px minimum touch target
              'w-12 h-12 min-w-[48px] min-h-[48px]',
              'max-md:w-13 max-md:h-13 max-md:min-w-[52px] max-md:min-h-[52px]',
              'rounded-xl shrink-0',
              'flex items-center justify-center',
              'transition-colors duration-150',
              // Enabled state
              message.trim() && !isDisabled
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed',
              // Focus state
              'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-2',
              'focus-visible:ring-2 focus-visible:ring-emerald-500/50'
            )}
            aria-label={getSendButtonLabel()}
            aria-disabled={!message.trim() || isDisabled}
          >
            {isSending ? (
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            ) : isRateLimited && countdownDisplay ? (
              <span className="text-xs font-semibold tabular-nums" aria-hidden="true">
                {countdownDisplay}
              </span>
            ) : (
              <Send className="w-5 h-5" aria-hidden="true" />
            )}
          </motion.button>
        </div>

        {/* Keyboard hint - visible on desktop only */}
        <p
          id="input-help"
          className={cn(
            'text-xs text-gray-400 text-center mt-2',
            'max-md:hidden',
            (message.trim() || isDisabled || isStreaming) && 'invisible'
          )}
        >
          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded-sm text-xs font-mono">Enter</kbd>
          {' '}to send,{' '}
          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded-sm text-xs font-mono">Shift+Enter</kbd>
          {' '}for new line
        </p>
      </form>
    </div>
  );
};

export default ChatInput;
